import { format } from "date-fns";

// Storage is UTC everywhere; this is the only place that knows the display zone.
// Phase 1 reads it from the user's profile; until then it is fixed.
export const APP_TIMEZONE = "Africa/Kampala";

function partsIn(date: Date, timeZone: string) {
	const parts = new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hourCycle: "h23",
	}).formatToParts(date);
	const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
	return {
		year: get("year"),
		month: get("month"),
		day: get("day"),
		hour: get("hour"),
		minute: get("minute"),
		second: get("second"),
	};
}

/**
 * A Date whose local fields equal the wall-clock time in `timeZone`.
 * Use it only for display with date-fns; never store or compare it.
 */
export function toZonedWallClock(date: Date, timeZone = APP_TIMEZONE): Date {
	const p = partsIn(date, timeZone);
	return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
}

/** The calendar day in `timeZone` as `YYYY-MM-DD` (the key for habit logs and reviews). */
export function localDateKey(date: Date = new Date(), timeZone = APP_TIMEZONE): string {
	const p = partsIn(date, timeZone);
	return `${p.year}-${String(p.month).padStart(2, "0")}-${String(p.day).padStart(2, "0")}`;
}

/** Format an instant for display in `timeZone` using a date-fns pattern. */
export function formatInZone(date: Date, pattern: string, timeZone = APP_TIMEZONE): string {
	return format(toZonedWallClock(date, timeZone), pattern);
}

/**
 * Formats a calendar day key (`YYYY-MM-DD`). The key is already a day in the
 * display zone, so it is built from its parts, never parsed as UTC.
 */
export function formatDay(key: string, pattern = "EEE d MMM"): string {
	const [y, m, d] = key.split("-").map(Number);
	return format(new Date(y, m - 1, d), pattern);
}

/** Adds whole days to a day key; calendar math only, so no DST or zone drift. */
export function addDays(key: string, days: number): string {
	const [y, m, d] = key.split("-").map(Number);
	const t = new Date(Date.UTC(y, m - 1, d + days));
	return t.toISOString().slice(0, 10);
}

/** A duration for display: 45m, 1h, 1h 30m. */
export function formatMinutes(min: number): string {
	if (min < 60) return `${min}m`;
	const h = Math.floor(min / 60);
	const m = min % 60;
	return m ? `${h}h ${m}m` : `${h}h`;
}

/** The UTC instant (ms) when day `key` starts in `timeZone`. */
export function dayStartMs(key: string, timeZone = APP_TIMEZONE): number {
	const [y, m, d] = key.split("-").map(Number);
	const guess = Date.UTC(y, m - 1, d);
	const p = partsIn(new Date(guess), timeZone);
	const wallAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
	return guess - (wallAsUtc - guess);
}

/** The Monday on or before day `key` (ISO weeks start on Monday). */
export function weekStart(key: string): string {
	const [y, m, d] = key.split("-").map(Number);
	const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
	return addDays(key, -((weekday + 6) % 7));
}
